<template>
  <BaseModal
    v-model="showModal"
    title="Historique des paiements"
    size="5xl"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center text-white shadow-lg">
          <BaseIcon name="clock" :size="24" />
        </div>
        <div>
          <h2 class="text-xl font-semibold text-gray-900">Historique des paiements</h2>
          <p class="text-sm text-gray-500">{{ payments.length }} paiement(s) enregistré(s)</p>
        </div>
      </div>
    </template>

    <div class="space-y-6">
      <!-- Résumé -->
      <div class="flex items-center justify-around bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div class="text-center">
          <span class="block text-xs text-gray-500 uppercase tracking-wide mb-1">Total facture</span>
          <span class="text-lg font-bold text-gray-900">{{ formatCurrency(totalAmount) }}</span>
        </div>
        <div class="w-px h-10 bg-gray-300"></div>
        <div class="text-center">
          <span class="block text-xs text-gray-500 uppercase tracking-wide mb-1">Total payé</span>
          <span class="text-lg font-bold text-green-600">{{ formatCurrency(paidAmount) }}</span>
        </div>
        <div class="w-px h-10 bg-gray-300"></div>
        <div class="text-center">
          <span class="block text-xs text-gray-500 uppercase tracking-wide mb-1">Reste</span>
          <span class="text-lg font-bold" :class="remainingAmount > 0 ? 'text-red-600' : 'text-green-600'">
            {{ formatCurrency(remainingAmount) }}
          </span>
        </div>
      </div>

      <!-- Liste des paiements -->
      <div v-if="payments.length > 0" class="space-y-3">
        <div
          v-for="(payment, index) in payments"
          :key="payment.id"
          class="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:border-primary hover:shadow-sm transition-all"
        >
          <div class="w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
            {{ index + 1 }}
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-3 mb-1">
              <span class="text-lg font-bold text-green-600">{{ formatCurrency(payment.amount) }}</span>
              <BaseBadge variant="gray" size="xs">{{ getPaymentMethodName(payment.payment_type) }}</BaseBadge>
            </div>
            <div class="flex flex-wrap gap-3 text-xs text-gray-500">
              <span class="flex items-center gap-1">
                <BaseIcon name="calendar" :size="12" />
                {{ formatDate(payment.payment_date) }}
              </span>
              <span v-if="payment.notes" class="flex items-center gap-1 truncate max-w-[200px]">
                <BaseIcon name="file-text" :size="12" />
                {{ payment.notes }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 flex-shrink-0">
            <button
              class="w-9 h-9 flex items-center justify-center bg-primary/10 text-primary rounded-lg hover:bg-primary hover:text-white transition-all"
              title="Imprimer le reçu"
              @click="printReceipt(payment)"
            >
              <BaseIcon name="printer" :size="16" />
            </button>
            <button
              v-if="canDelete"
              class="w-9 h-9 flex items-center justify-center bg-red-50 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-all"
              title="Supprimer"
              @click="confirmDeletePayment(payment)"
            >
              <BaseIcon name="trash" :size="16" />
            </button>
          </div>
        </div>
      </div>

      <!-- Aucun paiement -->
      <div v-else class="text-center py-10">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
          <BaseIcon name="file-text" :size="28" />
        </div>
        <p class="text-sm text-gray-500">Aucun paiement enregistré</p>
      </div>
    </div>

    <template #footer>
      <BaseButton variant="ghost" @click="closeModal">
        Fermer
      </BaseButton>
    </template>
  </BaseModal>

  <!-- Confirmation de suppression -->
  <BaseConfirmDialog
    v-model="showDeleteConfirm"
    title="Supprimer ce paiement ?"
    :message="`Cette action est irréversible. Le montant de ${formatCurrency(paymentToDelete?.amount)} sera retiré de la facture.`"
    variant="danger"
    confirm-text="Supprimer"
    :loading="isDeleting"
    @confirm="executeDelete"
    @cancel="cancelDelete"
  />
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import { saleInvoiceService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'
import { BaseModal, BaseButton, BaseIcon, BaseBadge, BaseConfirmDialog } from '@/components/base'

export default defineComponent({
  name: 'PaymentHistoryModal',
  components: {
    BaseModal,
    BaseButton,
    BaseIcon,
    BaseBadge,
    BaseConfirmDialog
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    invoiceId: {
      type: [Number, String],
      required: true
    },
    invoiceNumber: {
      type: String,
      default: ''
    },
    invoiceStatus: {
      type: String,
      default: 'pending'
    },
    payments: {
      type: Array,
      default: () => []
    },
    totalAmount: {
      type: Number,
      default: 0
    },
    paidAmount: {
      type: Number,
      default: 0
    },
    customerName: {
      type: String,
      default: ''
    },
    customerPhone: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'payment-deleted'],
  setup(props, { emit }) {
    const toastStore = useToastStore()
    const showDeleteConfirm = ref(false)
    const paymentToDelete = ref(null)
    const isDeleting = ref(false)

    const showModal = computed({
      get: () => props.show,
      set: (value) => {
        if (!value) {
          emit('close')
        }
      }
    })

    const remainingAmount = computed(() => {
      return (props.totalAmount || 0) - (props.paidAmount || 0)
    })

    const canDelete = computed(() => {
      return props.invoiceStatus !== 'paid'
    })

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    }

    const getPaymentMethodName = (type) => {
      const types = {
        'cash': 'Espèces',
        'card': 'Carte bancaire',
        'mobile_money': 'Mobile Money',
        'bank_transfer': 'Virement',
        'check': 'Chèque'
      }
      return types[type] || type
    }

    const printReceipt = (payment) => {
      const printWindow = window.open('', '_blank', 'width=400,height=600')

      const receiptContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Reçu de paiement</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; padding: 20px; max-width: 300px; margin: 0 auto; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 1px dashed #000; padding-bottom: 15px; }
            .header h1 { font-size: 16px; margin-bottom: 5px; }
            .header p { font-size: 11px; color: #666; }
            .section { margin-bottom: 15px; }
            .row { display: flex; justify-content: space-between; margin: 5px 0; }
            .label { color: #666; }
            .value { font-weight: bold; }
            .divider { border-top: 1px dashed #000; margin: 15px 0; }
            .total { font-size: 14px; }
            .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #666; }
            @media print { body { padding: 10px; } .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>RECU DE PAIEMENT</h1>
            <p>${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          <div class="section">
            <div class="row"><span class="label">Facture N°:</span><span class="value">${props.invoiceNumber}</span></div>
            <div class="row"><span class="label">Client:</span><span class="value">${props.customerName || 'N/A'}</span></div>
            ${props.customerPhone ? `<div class="row"><span class="label">Téléphone:</span><span class="value">${props.customerPhone}</span></div>` : ''}
          </div>
          <div class="divider"></div>
          <div class="section">
            <div class="row"><span class="label">Date paiement:</span><span class="value">${formatDate(payment.payment_date)}</span></div>
            <div class="row"><span class="label">Mode:</span><span class="value">${getPaymentMethodName(payment.payment_type)}</span></div>
            ${payment.notes ? `<div class="row"><span class="label">Notes:</span><span class="value">${payment.notes}</span></div>` : ''}
          </div>
          <div class="divider"></div>
          <div class="section total">
            <div class="row"><span class="label">MONTANT PAYE:</span><span class="value">${formatCurrency(payment.amount)}</span></div>
          </div>
          <div class="divider"></div>
          <div class="section">
            <div class="row"><span class="label">Total facture:</span><span class="value">${formatCurrency(props.totalAmount)}</span></div>
            <div class="row"><span class="label">Total payé:</span><span class="value">${formatCurrency(props.paidAmount)}</span></div>
            <div class="row"><span class="label">Reste à payer:</span><span class="value">${formatCurrency(remainingAmount.value)}</span></div>
          </div>
          <div class="footer"><p>Merci pour votre paiement!</p><p>Conservez ce reçu comme preuve de paiement</p></div>
          <div class="no-print" style="text-align: center; margin-top: 20px;"><button onclick="window.print()" style="padding: 10px 20px; cursor: pointer;">Imprimer</button></div>
        </body>
        </html>
      `

      printWindow.document.write(receiptContent)
      printWindow.document.close()
    }

    const confirmDeletePayment = (payment) => {
      paymentToDelete.value = payment
      showDeleteConfirm.value = true
    }

    const cancelDelete = () => {
      showDeleteConfirm.value = false
      paymentToDelete.value = null
    }

    const executeDelete = async () => {
      if (!paymentToDelete.value) return

      try {
        isDeleting.value = true
        await saleInvoiceService.deleteInvoicePayment(props.invoiceId, paymentToDelete.value.id)

        toastStore.addToast({
          type: 'success',
          message: 'Paiement supprimé avec succès',
          duration: 3000
        })

        showDeleteConfirm.value = false
        paymentToDelete.value = null
        emit('payment-deleted')
      } catch (error) {
        console.error('Erreur:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de la suppression du paiement',
          duration: 4000
        })
      } finally {
        isDeleting.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    watch(() => props.show, (newVal) => {
      if (!newVal) {
        showDeleteConfirm.value = false
        paymentToDelete.value = null
      }
    })

    return {
      showModal,
      showDeleteConfirm,
      paymentToDelete,
      isDeleting,
      remainingAmount,
      canDelete,
      formatCurrency,
      formatDate,
      getPaymentMethodName,
      printReceipt,
      confirmDeletePayment,
      cancelDelete,
      executeDelete,
      closeModal
    }
  }
})
</script>
